/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { useState, useEffect, Fragment } from "react";
import Popover from "@react/react-spectrum/Popover";
import OverlayTrigger from "@react/react-spectrum/OverlayTrigger";
import ButtonWrapper from "dc-preview-utils/src/components/Button/ButtonWrapper";
import Switch from "@react/react-spectrum/Switch";
import { pageViewModifiers, docViewModes } from "constants/enums";
import { Flyout } from "components/quickTools/common/Flyout";
import PrimaryPanelCss from "components/quickTools/Primary/PrimaryPanel.css";
import { apiNames } from "constants/apiNames";
import { apiFactory } from "api/external/ApiFactory";
import { TOOLTIP_LEFT_PLACEMENT, TOOLTIP_DELAY, FLYOUT_HIDE_TIMEOUT, KEYBOARD_SHORTCUTS } from "constants/constants";
import FitWidthIcon from "dc-icons/viewer2021/SDC_FitWidth_18_N.svg";
import FitPageIcon from "dc-icons/viewer2021/SDC_FitPageOneFullPage_18_N.svg";
import FixedWidthIcon from "dc-icons/viewer2021/SDC_SinglePage_18_N.svg";
import TwoColumnIcon from "dc-icons/viewer2021/SDC_TwoPageScrolling_18_N.svg";
import ZoomToPageLevelIcon from "dc-icons/viewer2021/SDC_ZoomToPageLevel_18_N.svg";
import FitWidthIconOld from "dc-icons/viewer2021/SDC_FitWidthScrollPages_18_N.svg";
import FullScreenIcon from "dc-icons/viewer2021/SDC_FullScreenMode_18_N.svg";
import FlyoutTabItem from "components/quickTools/common/FlyoutTabItem";
import FlyoutCss from "components/quickTools/common/Flyout.css";
import Rule from "@react/react-spectrum/Rule";
import { wait } from "utils/QuickToolsUtils";

let cancelWaitExecutor;
const PageViewSwitcher = props => {
  const {
    activeDocViewMode,
    activeViewMode,
    showFitPageMode,
    showFitWidthMode,
    showFixedWidthMode,
    showTwoColumnMode,
    showFullScreenMode,
    isViewWithScrolling,
    isCoverPageMode,
    intl,
    fitPageHandler,
    fitOneFullPageHandler,
    fitWidthHandler,
    fixWidthHandler,
    twoColumnHandler,
    fullScreenModeHandler,
    coverModeHandler,
    shouldDisable,
    defaultStyle,
    selectedStyle,
    shouldActivate,
    activeStyle,
    shouldEnableHover,
    hoverStyle,
    showZoomUI,
    isTwoColumnViewMode,
    showViewWithScrollButton,
    viewWithScrollingToggleHandler,
    shouldEnableFitOneFullPageMode,
    shortcutLabels,
    handlePanelToggle,
    showFlyover
  } = props;

  const [viewModes, setViewModes] = useState([]);
  const [documentViewModes, setDocumentViewModes] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isFocusOnPageViewSwitcher, setIsFocusOnPageViewSwitcher] = useState(false);
  const [initialAutoHide, setInitialAutoHide] = useState(false);

  const getFitPageComponentDetails = () => ({
    icon: showTwoColumnMode ? ZoomToPageLevelIcon : FitPageIcon,
    label: intl.formatMessage({
      id: showTwoColumnMode && isTwoColumnViewMode ? "modernviewer.arialabel.zoomToPageLevel" : "label.fitPage"
    }),
    tooltip: intl.formatMessage({
      id:
        showTwoColumnMode && isTwoColumnViewMode
          ? "modernviewer.tooltip.zoomToPageLevel"
          : "modernviewer.tooltip.fitPage"
    }),
    verbId: pageViewModifiers.FIT_PAGE,
    clickHandler: showTwoColumnMode && isTwoColumnViewMode ? fitPageHandler : fitOneFullPageHandler
  });

  const getFitWidthComponentDetails = () => ({
    icon: showTwoColumnMode ? FitWidthIcon : FitWidthIconOld,
    label: intl.formatMessage({ id: "label.fitWidth" }),
    tooltip: intl.formatMessage({ id: "modernviewer.tooltip.fitWidth" }),
    verbId: pageViewModifiers.FIT_WIDTH,
    clickHandler: fitWidthHandler
  });

  const getFixedWidthComponentDetails = () => ({
    icon: FixedWidthIcon,
    label: intl.formatMessage({ id: "arialabel.fixedWidth" }),
    tooltip: intl.formatMessage({ id: "modernviewer.tooltip.fixedWidth" }),
    verbId: pageViewModifiers.FIXED_WIDTH,
    clickHandler: fixWidthHandler
  });

  const getTwoColumnComponentDetails = () => ({
    icon: TwoColumnIcon,
    label: intl.formatMessage({ id: "modernviewer.arialabel.twoPageView" }),
    tooltip: intl.formatMessage({ id: "modernviewer.tooltip.twoPageView" }),
    verbId: docViewModes.TWO_COLUMN,
    clickHandler: () => {
      twoColumnHandler(true);
    }
  });

  const getSinglePageComponentDetails = () => ({
    icon: FitPageIcon,
    label: intl.formatMessage({ id: "modernviewer.arialabel.singlePageView" }),
    tooltip: intl.formatMessage({ id: "modernviewer.tooltip.singlePageView" }),
    verbId: docViewModes.CLASSIC_VIEW,
    clickHandler: () => {
      twoColumnHandler(false);
    }
  });

  const getViewModes = () => {
    const viewModes = [];
    showFitPageMode && viewModes.push(getFitPageComponentDetails());
    showFitWidthMode && viewModes.push(getFitWidthComponentDetails());
    showFixedWidthMode && viewModes.push(getFixedWidthComponentDetails());
    return viewModes;
  };

  const getDocumentViewModes = () => {
    const documentViewModes = [];
    showTwoColumnMode && documentViewModes.push(getSinglePageComponentDetails());
    showTwoColumnMode && documentViewModes.push(getTwoColumnComponentDetails());
    return documentViewModes;
  };

  const handlePageViewSwitcherButtonClick = event => {
    event.preventDefault();
    event.stopPropagation();
    handlePanelToggle("viewModeSwitcher", !showFlyover);
  };

  const setActiveIndexWrapper = () => {
    const _activeIndex = viewModes.findIndex(mode => mode.verbId === activeViewMode);
    if (_activeIndex === -1) {
      // nothing is active from the flyout menu; reset to default icon and tooltip
      setActiveIndex(-1);
    } else {
      setActiveIndex(_activeIndex);
    }
  };

  useEffect(() => {
    apiFactory.registerApi(apiNames.MODE_CHANGE, () => ({
      setFixedWidth: args => this.props.fixWidthHandler(args)
    }));
    setViewModes(getViewModes());
    setActiveIndexWrapper();
  }, []);

  useEffect(() => {
    setViewModes(getViewModes());
    setActiveIndexWrapper();
  }, [showFitPageMode, showFitWidthMode, showFixedWidthMode]);

  useEffect(() => {
    setDocumentViewModes(getDocumentViewModes());
    setViewModes(getViewModes());
  }, [showTwoColumnMode, isTwoColumnViewMode]);

  useEffect(() => {
    setActiveIndexWrapper();
  }, [activeViewMode, JSON.stringify(viewModes)]);

  let activeDocViewModeIndex;
  let buttonIcon = null;
  let tooltipString = null;
  if (documentViewModes && documentViewModes.length) {
    activeDocViewModeIndex = documentViewModes.findIndex(mode => mode.verbId === activeDocViewMode);
    if (activeDocViewModeIndex !== -1) {
      buttonIcon = documentViewModes[activeDocViewModeIndex].icon;
      tooltipString = documentViewModes[activeDocViewModeIndex].tooltip;
    }
  }

  const getSelectedVerbDocView = () =>
    activeDocViewModeIndex !== -1 ? documentViewModes[activeDocViewModeIndex].verbId : null;

  const handleDocumentViewModeClick = (verbId = null) => {
    const selectedVerb = getSelectedVerbDocView();
    if (verbId && selectedVerb !== verbId) {
      const index = documentViewModes.findIndex(el => el.verbId === verbId);
      documentViewModes[index].clickHandler();
    }
    handlePanelToggle("viewModeSwitcher", false);
  };

  const getSelectedVerbViewMode = () => (activeIndex !== -1 ? viewModes[activeIndex].verbId : null);

  const handleViewModeClick = (verbId = null) => {
    // Not checking for selected verb, since RepeatedSelection is allowed
    if (verbId) {
      const index = viewModes.findIndex(el => el.verbId === verbId);
      viewModes[index].clickHandler();
    }
    handlePanelToggle("viewModeSwitcher", false);
  };

  const handleFullScreenModeClick = () => {
    fullScreenModeHandler();
    handlePanelToggle("viewModeSwitcher", false);
  };

  const getDocViewModeGroup = () => {
    const itemList = [];
    showTwoColumnMode &&
      itemList.push(
        <Fragment>
          {documentViewModes.map(verb => (
            <FlyoutTabItem
              icon={verb.icon}
              verbClickHandler={() => handleDocumentViewModeClick(verb.verbId)}
              label={verb.label}
              tooltip={{
                text: verb.tooltip
              }}
              isSelected={getSelectedVerbDocView() === verb.verbId}
            />
          ))}
          {activeDocViewMode === docViewModes.TWO_COLUMN ? (
            <Switch
              checked={isCoverPageMode}
              label={intl.formatMessage({ id: "modernviewer.arialabel.coverPage" })}
              onChange={coverModeHandler}
              aria-label={intl.formatMessage({ id: "modernviewer.arialabel.coverPage" })}
              labelClassName={PrimaryPanelCss.switchLabel}
              title={intl.formatMessage({ id: "modernviewer.tooltip.coverPage" })}
              className={PrimaryPanelCss.CoverModeSwitch}
              quiet
              aria-hidden="true"
            />
          ) : null}
          {shouldEnableFitOneFullPageMode &&
          activeDocViewMode === docViewModes.CLASSIC_VIEW &&
          showViewWithScrollButton ? (
            <Switch
              checked={isViewWithScrolling}
              label={intl.formatMessage({ id: "modernviewer.arialabel.viewWithScrolling" })}
              onChange={viewWithScrollingToggleHandler}
              aria-label={intl.formatMessage({ id: "modernviewer.arialabel.viewWithScrolling" })}
              labelClassName={PrimaryPanelCss.switchLabel}
              title={intl.formatMessage({ id: "modernviewer.tooltip.viewWithScrolling" })}
              className={PrimaryPanelCss.CoverModeSwitch}
              quiet
              aria-hidden="true"
            />
          ) : null}
        </Fragment>
      );
    return itemList;
  };

  const getViewModeGroup = () => {
    const itemList = [];
    showZoomUI &&
      itemList.push(
        viewModes.map(verb => (
          <FlyoutTabItem
            icon={verb.icon}
            verbClickHandler={() => handleViewModeClick(verb.verbId)}
            label={verb.label}
            tooltip={{
              text: verb.tooltip
            }}
            isSelected={getSelectedVerbViewMode() === verb.verbId}
          />
        ))
      );
    return itemList;
  };

  const getFullScreenModeItem = () => {
    const itemList = [];
    showFullScreenMode &&
      itemList.push(
        <FlyoutTabItem
          icon={FullScreenIcon}
          verbClickHandler={() => handleFullScreenModeClick()}
          shortcutLabel={(shortcutLabels || {})[KEYBOARD_SHORTCUTS.FULL_SCREEN]}
          label={intl.formatMessage({ id: "modernviewer.ariaLabel.fullScreenView" })}
          tooltip={{
            text: intl.formatMessage({ id: "modernviewer.tooltip.fullScreenView" })
          }}
          isSelected={getSelectedVerbViewMode() === docViewModes.FULL_SCREEN}
        />
      );
    return itemList;
  };

  const getPopoverComponentsList = () => {
    const viewModeGroup = getViewModeGroup();
    const docViewModeGroup = getDocViewModeGroup();
    const fullScreeModeItem = getFullScreenModeItem();
    const groupList = [docViewModeGroup, viewModeGroup, fullScreeModeItem];
    return groupList.filter(item => item && item.length > 0);
  };

  const HorizontalSeparator = () => (
    <Rule role="presentation" variant="medium" className={PrimaryPanelCss.divideRule} />
  );

  const shouldAddSeparatorView = index => index !== 0;
  const PopoverComponent = () => {
    const popoverComponents = [];
    const popoverComponentsList = getPopoverComponentsList();
    popoverComponentsList.forEach((component, i) => {
      if (shouldAddSeparatorView(i)) {
        popoverComponents.push(<HorizontalSeparator />);
      }
      popoverComponents.push(component);
    });
    return (
      <div role="tablist" className={FlyoutCss.FlyoutTabList}>
        {popoverComponents}
      </div>
    );
  };

  const getCrossOffsetForFlyout = () => (props.showZoomControl ? 0 : -50);

  const keyDownHandler = () => {
    setInitialAutoHide(false);
  };

  const mouseEnterHandler = () => {
    cancelWaitExecutor && cancelWaitExecutor();
    setIsFocusOnPageViewSwitcher(true);
    setInitialAutoHide(true);
  };

  const mouseLeaveHandler = () => {
    const waitExecutor = wait(FLYOUT_HIDE_TIMEOUT);
    const waitToSetMouseLeave = waitExecutor.promise;
    cancelWaitExecutor = waitExecutor.cancel;
    waitToSetMouseLeave
      .then(() => {
        setIsFocusOnPageViewSwitcher(false);
      })
      .catch(() => {});
  };

  const renderPageViewSwitcher = () => (
    <div
      className={PrimaryPanelCss.PageViewSwitcherWrappper}
      data-testid="ChangeViewMode"
      onMouseEnter={mouseEnterHandler}
      onMouseLeave={mouseLeaveHandler}
      onKeyDown={keyDownHandler}
    >
      <OverlayTrigger
        trigger="click"
        crossOffset={getCrossOffsetForFlyout()}
        onClick={handlePageViewSwitcherButtonClick}
        show={showFlyover}
      >
        <div>
          <ButtonWrapper
            id="change-view-mode"
            icon={
              buttonIcon === null ? (activeIndex === -1 ? FixedWidthIcon : viewModes[activeIndex].icon) : buttonIcon
            }
            shouldDisable={!!shouldDisable}
            ariaLabel="arialabel.changeViewMode"
            tooltipString={
              tooltipString === null
                ? activeIndex === -1
                  ? "modernviewer.tooltip.fixedWidth"
                  : viewModes[activeIndex].tooltip
                : tooltipString
            }
            tooltipPlacement={TOOLTIP_LEFT_PLACEMENT}
            tooltipDelay={TOOLTIP_DELAY}
            defaultStyle={defaultStyle}
            isSelected={activeIndex !== -1 || showFlyover}
            selectedStyle={selectedStyle}
            shouldActivate={shouldActivate}
            activeStyle={activeStyle}
            shouldEnableHover={shouldEnableHover}
            hoverStyle={hoverStyle}
            holdAffordance
            shouldDisableTooltip={showFlyover}
          />
        </div>
        <Popover isDialog={false} className={PrimaryPanelCss.pageViewSwitcherPopOver}>
          <Flyout
            closeFlyout={() => {
              const flyoutTrigger = document.getElementById("change-view-mode");
              if (flyoutTrigger) {
                flyoutTrigger.focus();
              }
              handlePanelToggle("viewModeSwitcher");
              setIsFocusOnPageViewSwitcher(false); // resetting on closing flyout
              setInitialAutoHide(false);
            }}
            component={PopoverComponent}
            forceShowFlyout={isFocusOnPageViewSwitcher}
            initialAutoHide={initialAutoHide}
          />
        </Popover>
      </OverlayTrigger>
    </div>
  );

  return (
    <Fragment>
      {(viewModes && viewModes.length) || (documentViewModes && documentViewModes.length)
        ? renderPageViewSwitcher()
        : null}
    </Fragment>
  );
};

export { PageViewSwitcher };
